import React, { useContext, useEffect, useMemo, useState } from "react";
import { Button, Card, Col, Modal, Row, Spinner } from "react-bootstrap";
import FlowrateChartGroup from "../../Components/FlowrateChartGroup/FlowrateChartGroup";
import { textFilter } from "react-bootstrap-table2-filter";
import DataTable from "../../Components/Beverages/StepReportTable/DataTable";
import RangeIndicator from "../../Components/RangeIndicator/RangeIndicator";
import { FaArrowLeft } from "react-icons/all";
import Badge from "react-bootstrap/Badge";
import AlertsModal from "./AlertsModal";
import {
  formatDateAndTime,
  formatDateTime,
  formatDescriptionText,
  removeFractionDigits,
  turnAlertsIntoObjectArray,
} from "../../General/general";
import SensorCharts from "./SensorCharts";
import "./ObjectDetailsPage.css";
import { IoMdArrowDropdown, IoMdArrowDropup } from "react-icons/io";
import {
  conversionUnitFactors,
  unitLabels,
  unitMeasurementSystem,
  unitSymbols,
} from "../../enums";
import { useHistory, useLocation, useParams } from "react-router-dom";
import { store } from "../../Store/store";
import { BeveragesLineService } from "./BeveragesLineService";
import {
  getSubStepMapping,
  formatSubStepInfo,
  PRODUCTION_LINE_TO_SITE,
  createSandstonDynamicMapping,
  formatSandstonSubStepInfo,
} from "../../constants/subStepMappings";

export default function ObjectDetailsPage({
  setMoreDetailsButtonClicked,
  healthFormatter: propsHealthFormatter,
  alertsColumns: propsAlertsColumns,
  convertLitersToCubic: propsConvertLitersToCubic,
  cubicUnitLabel: propsCubicUnitLabel,
  unit: propsUnit,
}) {
  const { objectId } = useParams();
  const history = useHistory();
  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  const globalState = useContext(store);

  // Initialize services and data directly
  const beveragesLineService = new BeveragesLineService(
    process.env.REACT_APP_API_URL
  );
  const selectedProductionLineId =
    globalState.state.selectedProductionLine?.id ||
    JSON.parse(globalState.state.selectedProductionLine)?.id;
  const unit = propsUnit || globalState.state.unit;
  const isImperial = unit === unitMeasurementSystem.IMPERIAL;
  const cubicUnitLabel =
    propsCubicUnitLabel ||
    (isImperial ? unitLabels.GALLONS : unitLabels.CUBIC_METERS);
  const [objectDetails, setObjectDetails] = useState(null);
  const [objectName, setObjectName] = useState("");
  const [startTime, setStartTime] = useState("");
  const [startTimeFormatted, setStartTimeFormatted] = useState("");
  const [endTime, setEndTime] = useState("");
  const [duration, setDuration] = useState(0);
  const [recipe, setRecipe] = useState("");
  const [kpi1, setKpi1] = useState(0);
  const [health, setHealth] = useState(0);
  const [recuperatedVolume, setRecuperatedVolume] = useState(0);
  const [freshWaterVolume, setFreshWaterVolume] = useState(0);
  const [isActive, setIsActive] = useState(false);
  const [stepGroup, setStepGroup] = useState("");
  const [alerts, setAlerts] = useState(0);
  const [line, setLine] = useState("");

  const convertLitersToCubic =
    propsConvertLitersToCubic ||
    ((value, fractionDigits) => {
      return isImperial
        ? Math.round(value * conversionUnitFactors.LITERS_TO_GALLONS).toFixed(
            fractionDigits
          )
        : (value / 1000).toFixed(fractionDigits);
    });

  const healthFormatter =
    propsHealthFormatter ||
    ((cell) => {
      return <div>{cell}</div>;
    });

  // Default alerts columns if not provided via props
  const alertsColumns = propsAlertsColumns || [
    { dataField: "id", title: "ID", hidden: true },
    {
      dataField: "startTime",
      title: "Start Time",
      sort: true,
      formatter: formatDateTime,
    },
    {
      dataField: "endTime",
      title: "End Time",
      sort: true,
      formatter: formatDateTime,
    },
    { dataField: "type", title: "Type", sort: true },
    {
      dataField: "message",
      title: "Description",
      sort: true,
      formatter: (cell) => formatDescriptionText(cell, unit),
    },
  ];
  const [sensorCharts, setSensorCharts] = useState([]);
  const [sensorChartsUnitSymbol, setSensorChartsUnitSymbol] = useState(null);
  const [sensorResponse, setSensorResponse] = useState([]);
  const largeFilterStyle = {
    width: 280,
    margin: "0 auto",
    height: 30,
    fontSize: 12,
  };
  const filterStyle = { margin: "0 auto", height: 30, fontSize: 12 };
  const smallFilterStyle = {
    width: 100,
    margin: "0 auto",
    height: 30,
    fontSize: 12,
  };
  const headerStyle = { fontSize: 15, border: "none" };
  const colStyle = {
    fontSize: 15,
    fontWeight: "bold",
    background: "white",
    verticalAlign: "center",
  };
  const [showModal, setShowModal] = useState(false);
  const [showChartModal, setShowChartModal] = useState(false);
  const [lineDetails, setLineDetails] = useState([]);
  const [lineDetailsPreRinseMerged, setLineDetailsPreRinseMerged] = useState(
    []
  );
  const [showPreRinseMerged, setShowPreRinseMerged] = useState(true);
  const [preRinseCount, setPreRinseCount] = useState(0);
  const [alertsData, setAlertsData] = useState([]);
  const [isStepAlertsRequestInProgress, setIsStepAlertsRequestInProgress] =
    useState(false);
  const [
    isObjectDetailsRequestInProgress,
    setIsObjectDetailsRequestInProgress,
  ] = useState(false);
  const [isObjectDataRequestInProgress, setIsObjectDataRequestInProgress] =
    useState(false);
  const [isSensorChartsRequestInProgress, setIsSensorChartsRequestInProgress] =
    useState(false);

  // Error states for each request type
  const [stepAlertsRequestError, setStepAlertsRequestError] = useState(false);
  const [objectDetailsRequestError, setObjectDetailsRequestError] =
    useState(false);
  const [objectDataRequestError, setObjectDataRequestError] = useState(false);
  const [sensorChartsRequestError, setSensorChartsRequestError] =
    useState(false);
  const [verticalLineTimeStamps, setVerticalLineTimeStamps] = useState([]);
  const [cipLineTimestampsDetailed, setCipLineTimestampsDetailed] = useState(
    []
  );

  const getStepAlerts = async (
    lineId,
    stepGroupId,
    selectedProductionLineId
  ) => {
    try {
      setAlertsData([]);
      setIsStepAlertsRequestInProgress(true);
      setStepAlertsRequestError(false);
      const response = await beveragesLineService.getStepGroupAlerts(
        lineId,
        stepGroupId,
        selectedProductionLineId
      );
      setIsStepAlertsRequestInProgress(false);
      setAlertsData(response.data);
    } catch (error) {
      setIsStepAlertsRequestInProgress(false);
      setStepAlertsRequestError(true);
      console.error(error);
    }
  };

  const volumeFormatter = (cell, row) => {
    if (
      !row.minVolume ||
      !row.maxVolume ||
      (!showPreRinseMerged &&
        preRinseCount > 0 &&
        row.stepGroup === "Pre Rinse")
    ) {
      if (cell === 0) return 0;
      const converted = parseFloat(convertLitersToCubic(cell, 2));

      if (isImperial) {
        return converted.toLocaleString(undefined, {
          minimumFractionDigits: 0,
          maximumFractionDigits: 0,
        });
      } else {
        return converted.toLocaleString();
      }
    }
    const minVolume =
      row.minVolume === 0
        ? 0
        : parseFloat(convertLitersToCubic(row.minVolume, 2));
    const volume = cell === 0 ? 0 : parseFloat(convertLitersToCubic(cell, 2));
    const maxVolume =
      row.maxVolume === 0
        ? 0
        : parseFloat(convertLitersToCubic(row.maxVolume, 2));
    return (
      <div className={"pl-5 pr-5"}>
        <RangeIndicator
          minValue={minVolume}
          value={volume}
          maxValue={maxVolume}
        />
      </div>
    );
  };

  const cubicFormatter = (cell) => {
    if (cell === 0) return 0;
    const converted = parseFloat(convertLitersToCubic(cell, 2));

    if (isImperial) {
      return converted.toLocaleString(undefined, {
        minimumFractionDigits: 0,
        maximumFractionDigits: 0,
      });
    } else {
      return converted.toLocaleString();
    }
  };

  const tableDurationFormatter = (cell, row) => {
    if (
      !row.minDuration ||
      !row.maxDuration ||
      (!showPreRinseMerged &&
        preRinseCount > 0 &&
        row.stepGroup === "Pre Rinse")
    ) {
      return (
        <div style={{ opacity: 0.7 }} className="p-3">
          {removeFractionDigits(cell / 60)}
        </div>
      );
    }
    const minDuration = removeFractionDigits(row.minDuration / 60);
    const duration = removeFractionDigits(cell / 60);
    const maxDuration = removeFractionDigits(row.maxDuration / 60);
    return (
      <div className={"pl-5 pr-5"}>
        <RangeIndicator
          minValue={minDuration}
          value={duration}
          maxValue={maxDuration}
        />
      </div>
    );
  };
  const alertsFormatter = (cell, row) => {
    if (cell > 0) {
      return (
        <div className="justify-content-center align-items-center">
          <div className={"d-inline-block"}>{cell}</div>
          {showPreRinseMerged &&
          preRinseCount > 0 &&
          row.stepGroup === "Pre Rinse" ? null : (
            <div className={"d-inline-block"}>
              <Button
                style={{ height: "24.5px" }}
                className="pl-1 pr-0 pt-0 pb-0"
                onClick={async () => {
                  await getStepAlerts(
                    objectId,
                    row.id,
                    selectedProductionLineId
                  );
                  setShowModal(true);
                }}
                size={"sm"}
                variant={"link"}
              >
                <h6>
                  <Badge pill variant={"danger"}>
                    {" "}
                    i
                  </Badge>
                </h6>
              </Button>
            </div>
          )}
        </div>
      );
    } else {
      return 0;
    }
  };

  const stepGroupFormatter = (() => {
    const preRinseEncountered = new Set();

    return (cell, row) => {
      if (cell === "Pre Rinse") {
        if (!preRinseEncountered.has(cell) && preRinseCount > 0) {
          preRinseEncountered.add(cell);
          return (
            <Button
              style={{ color: "#212529" }}
              className="p-0"
              variant="link"
              onClick={() => setShowPreRinseMerged(!showPreRinseMerged)}
            >
              <div style={{ display: "inline-block" }}>
                {showPreRinseMerged ? (
                  <IoMdArrowDropdown />
                ) : (
                  <IoMdArrowDropup />
                )}
              </div>
              <div
                style={{
                  display: "inline-block",
                  fontWeight: "bold",
                  fontSize: "15px",
                }}
              >
                {cell}
              </div>
            </Button>
          );
        } else {
          return <div>{cell}</div>;
        }
      }
      return cell;
    };
  })();

  const handleShow = () => setShowChartModal(true);
  const handleClose = () => setShowChartModal(false);

  const columns = useMemo(
    () => [
      {
        dataField: "id",
        title: "ID",
        hidden: true,
      },
      {
        dataField: "stepGroup",
        title: "Step Group",
        sort: true,
        filter: textFilter({ style: filterStyle }),
        formatter: stepGroupFormatter,
      },
      {
        dataField: "solution",
        title: "Solution",
        sort: true,
        filter: textFilter({ style: filterStyle }),
        formatter: (cell) => (
          <span style={{ textTransform: "capitalize" }}>{cell}</span>
        ),
      },
      {
        dataField: "startTime",
        title: "Start Time [HH:mm:ss]",
        sort: true,
        formatter: formatDateTime,
        filter: textFilter({ style: filterStyle }),
      },
      {
        dataField: "recycledVolume",
        title: `Returned Volume [${cubicUnitLabel}]`,
        formatter: cubicFormatter,
        sort: true,
        filter: textFilter({ style: filterStyle }),
      },
      {
        dataField: "drainedVolume",
        title: `Drained Volume [${cubicUnitLabel}]`,
        formatter: cubicFormatter,
        sort: true,
        filter: textFilter({ style: filterStyle }),
      },
      {
        dataField: "volume",
        sort: true,
        title: `Supply Volume [${cubicUnitLabel}]`,
        formatter: volumeFormatter,
        filter: textFilter({ style: largeFilterStyle }),
      },
      {
        dataField: "duration",
        sort: true,
        title: "Duration [Minutes]",
        formatter: tableDurationFormatter,
        filter: textFilter({ style: largeFilterStyle }),
      },
      {
        dataField: "alerts",
        sort: true,
        title: "Alerts",
        formatter: alertsFormatter,
        filter: textFilter({ style: smallFilterStyle }),
      },
      {
        dataField: "health",
        title: "Score",
        formatter: healthFormatter,
        sort: true,
        filter: textFilter({ style: filterStyle }),
      },
    ],
    [unit, showPreRinseMerged, preRinseCount]
  );

  const calculateRecuperatedWater = () => {
    if (!recuperatedVolume || !freshWaterVolume) {
      return 0;
    }
    return ((recuperatedVolume / freshWaterVolume) * 100).toFixed(2);
  };

  const covertFreshWaterLitersToCubicMeters = () => {
    return (kpi1 / 1000).toFixed(1);
  };

  const handleStepGroupDisplayInfo = () => {
    return isActive ? stepGroup : "Done";
  };

  const addAlertMarkers = (data, alertTimestamps) => {
    let newData;
    let alertTimesArray = alertTimestamps;
    newData = data.map((obj, index) => {
      let timeSeriesNewArray = [];
      timeSeriesNewArray = obj.timeSeriesData.map((timeObj, index) => {
        let indexFound = alertTimesArray?.indexOf(timeObj?.x);
        if (indexFound !== -1 && obj.name === "Alerts") {
          return {
            ...timeObj,
            marker: {
              symbol:
                "url(data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAB4AAABOCAYAAAA3vqZVAAAAAXNSR0IArs4c6QAABYFJREFUaEPtl2tIlFkYx89zzutcbBxbV8t2Xahsl4woY8XCDFZqpVhltrQEYT8Eu1psRmEUuAVDsNGFiMDEjVrtRjR+kJEoCslK1ClcWjL31gfdbrPMrrpeRp2Z95xnmWlncHLeuWhDX15hPvm+z+/8/+c5z/+8QN7RH7wjLokJjIQAqarSkNHR1+8tXEhISgqSwUEPmM0iFhERwVhRkeArODQEfOXKrWg01tOJCUYAUGi1iGlpY2Rw0JQgxM9EqxVQWemJZgFhwVhVpeUFBedIf/97xOHIJi9epOOjRxTGx33vocFAMD0dISeHk2XLJogs10tdXd/B2bMR4YpgtFgM3On8EdvbvwSrldKBAe+z/l+QKCQEMTWViOJiD0lN3Z8wZ84PYDZPhlMeEowWC5Ndrl9IY+Ni+uCBBGNjvhqoUMlfBA0GFGvzPHLp1t91L19mh9v3IDAiAmltXe5xOq/Ta9cywGoF8r+tXqaSPUEL0uuRl5cjFhc7NMnJi6CgIKTyYLDFouHJyeuxpcUKzc0SffUK/EUjdeHU53DBAhQlJTKWln6bYDA0Qk7OtD0Pqufq6FhORkZOsEOHCml3N/XbGwnq3wEv3P8sz85GUVs7wbKyellKSh4AyFN3KlATzWbKy8v7eFPTB6y2llG73ac2WuibcJGRQXhNjWDFxU6WkZESFixKSlzy4cOSdOsWgZERXzPNFIxGIxFlZUirqwW123VQUKCg2GJhmJnplqurKevqIuByzQ6s1xOxbh2yo0cFDA9HAC9a5Jb37qXs4UMCbvfswDodEfn5yI4f5zA8rFdWbDZTNJk8ck0NZffuERgfnx04MZGIoiJB9+8foaOjaWHBoqxsXD55UiM1NxMYGJhVc+G8ecgrKzndsuUn9vTpWti2jYfuakQQPT0VcmfnKXbkiI4+ezY78JIlXN6166YmN3cb5OVNvDn0ggdIW5vk6u1dLF2+/Cu12WZ1jjEnR6a5uR9BXd1foSbttNOCZrPEnzyZhBs3WKz77D9+KEkEN23yMJ1OD01NQRb7FzEd7A2IsbG/oaFhLm1vj8luP1hkZiKWlLikvj5D1GDfmLx4cY7o7f2XHDsmhQuHNy30g3lhocDNm20JO3fmg0KoKcYi2u1OceCAhk5ORgyKoIAAQDx40MMYSwKz2a2UyYpgnph4ndy8+Tmtr6fIue+5SLEIAIRnZQmyfftjyen8NOo8nro6bGuT+PPnf8COHQv9mRwO7P0fMoZiz55RKSkpLZzasNvnTSuxdOnX5Pz5WnL3rgQeT8hGCwQJABHp6dyzYcPH+kuX+iJd+MJf9iwWxnt6voI7d85BRwcLlVaBI+S99qxf75Q0mrlKnRxycimtEM1mnXA4RsiFCxI4nUGqpywEyerVHJKTP4Tbtx2R1EZ1UnyWr1r1Aq5cSSdNTYCIgSYLqDUakXzxxZ/06tXFSscn7MhUVN3QoCNa7RDu3q3FgQHvgQncOr0gUVjoFkJ8pmlttUWjNirFvoHiVZ2fP0TOnEmClhZA/noK+jqZUiQm00na3HwACIn6Mybqmw1aLBoxOvqY7Nv3ifdzxgdmDLnJNCnZ7e9DV9e0BAqnPmqwT7nVukWcPm0h9+9T4Bxw/nzkGzfu1TQ2no7WYsWQCFcAOzsrhM12AurqktCb10VFbrZihT7WL8Wo9zhwdW1rk4jNloQ22z/C4QCxZs03mlOnzseqNmawz27vTaWlxY1uN2e9vcZIozGmkIikQO7vv8S6u7+H0tLfIj37VsFe1QCBtJwRO6aunhFB4SUV/DbdDFtLtVq1Om4OqM0VN2tndNmLx2rUPY6HqyFrqlarVsfNAbW54matOqvV5lKbK24OqM0VN2vVyaU2l9pccXPgPx4Qb22HevBVAAAAAElFTkSuQmCC)",
            },
            enabled: true,
            radius: 4,
            zIndex: 5,
          };
        } else {
          return timeObj;
        }
      });
      return { ...obj, timeSeriesData: timeSeriesNewArray };
    });
    return newData;
  };

  const getMetersFlowRateData = async (
    selectedProductionLineId,
    selectedObjectId,
    startDate,
    endDate,
    unit,
    line
  ) => {
    try {
      setIsSensorChartsRequestInProgress(true);
      setSensorChartsRequestError(false);
      await getVerticalLineTimeStamps(
        selectedProductionLineId,
        selectedObjectId,
        startDate,
        endDate
      );
      const alertTimeStamps = await getAlertTimeStamps(
        selectedProductionLineId,
        selectedObjectId,
        startDate,
        endDate
      );
      const response = await beveragesLineService.getGroupedMetersFlowRateData(
        selectedProductionLineId,
        line,
        startDate,
        endDate
      );

      let group3Sensors = response.data.find((obj) => obj?.group === "3");
      let group2Sensors = response.data.find((obj) => obj?.group === "2");
      const isImperial = unit === unitMeasurementSystem.IMPERIAL;

      if (group2Sensors && group2Sensors.sensors && isImperial) {
        group2Sensors.sensors = group2Sensors.sensors.map((sensor) => {
          if (sensor.timeSeriesData) {
            if (sensor.unitSymbol === unitSymbols.CELSIUS) {
              sensor.timeSeriesData = sensor.timeSeriesData.map((dataPoint) => {
                if (Array.isArray(dataPoint) && dataPoint.length >= 2) {
                  return [
                    dataPoint[0],
                    conversionUnitFactors.CELSIUS_TO_FAHRENHEIT(dataPoint[1]),
                  ];
                }
                return dataPoint;
              });
              sensor.unitSymbol = unitSymbols.FAHRENHEIT;
            } else if (sensor.unitSymbol === unitSymbols.MILLISIEMENS) {
              // Convert millisiemens to microsiemens
              sensor.timeSeriesData = sensor.timeSeriesData.map((dataPoint) => {
                if (Array.isArray(dataPoint) && dataPoint.length >= 2) {
                  return [
                    dataPoint[0],
                    dataPoint[1] *
                      conversionUnitFactors.MILLISIEMENS_TO_MICROSIEMENS,
                  ];
                }
                return dataPoint;
              });
              sensor.unitSymbol = unitSymbols.MICROSIEMENS;
            }
          }
          return sensor;
        });
      }

      if (group3Sensors && group3Sensors.sensors) {
        const isCocaCola = process.env.REACT_APP_COMPANY_NAME === "Coca-Cola";
        if (isImperial) {
          group3Sensors.sensors = group3Sensors.sensors.map((sensor) => {
            if (sensor.timeSeriesData && !isCocaCola) {
              sensor.timeSeriesData = sensor.timeSeriesData.map((dataPoint) => {
                if (Array.isArray(dataPoint) && dataPoint.length >= 2) {
                  return [
                    dataPoint[0],
                    dataPoint[1] *
                      conversionUnitFactors.LITERS_PER_HOURS_TO_GALLONS_PER_MINUTE,
                  ];
                }
                return dataPoint;
              });
            }
            sensor.unitSymbol = unitSymbols.GALLONS_PER_MINUTE;
            sensor.unitLabel = unitLabels.GALLONS_PER_MINUTE;

            return sensor;
          });
        } else {
          group3Sensors.sensors = group3Sensors.sensors.map((sensor) => {
            if (sensor.timeSeriesData && isCocaCola) {
              sensor.timeSeriesData = sensor.timeSeriesData.map((dataPoint) => {
                if (Array.isArray(dataPoint) && dataPoint.length >= 2) {
                  return [
                    dataPoint[0],
                    dataPoint[1] /
                      conversionUnitFactors.LITERS_PER_HOURS_TO_GALLONS_PER_MINUTE,
                  ];
                }
                return dataPoint;
              });
            }
            sensor.unitSymbol = unitSymbols.LITERS_PER_HOUR;
            sensor.unitLabel = unitLabels.LITERS_PER_HOUR;
            return sensor;
          });
        }
      }

      group3Sensors.sensors.push({
        name: "Alerts",
        label: "Alerts",
        unitSymbol: isImperial
          ? unitLabels.GALLONS_PER_MINUTE
          : unitLabels.LITERS_PER_HOUR,
        unitLabel: isImperial
          ? unitLabels.GALLONS_PER_MINUTE
          : unitLabels.LITERS_PER_HOUR,
        minVal: 0,
        maxVal: 0,
        tags: [],
        timeSeriesData: alertTimeStamps.map((alertTimeStamp) => [
          alertTimeStamp,
          0.0,
        ]),
      });
      let modifiedSensors = addAlertMarkers(
        turnAlertsIntoObjectArray(group3Sensors.sensors),
        alertTimeStamps
      );
      setSensorCharts(
        modifiedSensors.map((sensor) => ({
          name: sensor.label,
          data: sensor.timeSeriesData,
          tags: sensor.tags,
        }))
      );

      let sandstonMapping = null;
      let isUsingSandstonDynamic = false;

      const productionLineId =
        globalState.state.selectedProductionLine?.id ||
        JSON.parse(globalState.state.selectedProductionLine)?.id;
      const site =
        PRODUCTION_LINE_TO_SITE[productionLineId] ||
        (productionLineId?.toLowerCase().includes("sandston")
          ? "SANDSTON"
          : null);

      if (site === "SANDSTON" && objectName && recipe) {
        try {
          const stepPairsResponse =
            await beveragesLineService.getSandstonStepPairs(
              selectedProductionLineId,
              recipe,
              objectName
            );

          if (stepPairsResponse.data && stepPairsResponse.data.length > 0) {
            sandstonMapping = createSandstonDynamicMapping(
              stepPairsResponse.data
            );
            isUsingSandstonDynamic = true;
          }
        } catch (error) {
          console.error(
            "Failed to fetch Sandston step pairs, using static mapping:",
            error
          );
        }
      }

      const sensorResponseData = response.data.map((obj) => {
        if (obj.group === "3") {
          return { ...group3Sensors, sensors: modifiedSensors };
        } else if (obj.group === "2" && isImperial) {
          return group2Sensors;
        } else if (obj.group === "1") {
          // Use the pre-fetched mapping or fallback to static
          const subStepMapping =
            sandstonMapping ||
            getSubStepMapping(globalState.state.selectedProductionLine);

          // Enhance return steps with parsed info
          const enhancedReturnSteps = {};
          if (obj.returnSteps) {
            Object.keys(obj.returnSteps).forEach((key) => {
              const step = obj.returnSteps[key];
              const subStepInfo = isUsingSandstonDynamic
                ? formatSandstonSubStepInfo(step.name, subStepMapping)
                : formatSubStepInfo(step.name, subStepMapping);

              enhancedReturnSteps[key] = {
                ...step,
                subStepInfo: subStepInfo,
              };
            });
          }

          // Enhance valves with parsed info
          const enhancedValves = {};
          if (obj.valves) {
            Object.keys(obj.valves).forEach((key) => {
              const valve = obj.valves[key];
              const subStepInfo = isUsingSandstonDynamic
                ? formatSandstonSubStepInfo(valve.name, subStepMapping)
                : formatSubStepInfo(valve.name, subStepMapping);

              enhancedValves[key] = {
                ...valve,
                subStepInfo: subStepInfo,
              };
            });
          }

          return {
            ...obj,
            subStepMapping: subStepMapping,
            returnSteps: enhancedReturnSteps,
            valves: enhancedValves,
          };
        } else {
          return obj;
        }
      });
      setSensorResponse(sensorResponseData);
      setSensorChartsUnitSymbol(group3Sensors.sensors[0]?.unitSymbol);
      setIsSensorChartsRequestInProgress(false);
    } catch (error) {
      setIsSensorChartsRequestInProgress(false);
      setSensorChartsRequestError(true);
      console.error(error);
    }
  };

  const getVerticalLineTimeStamps = async (
    selectedProductionLineId,
    selectedObjectId,
    startDate,
    endDate
  ) => {
    try {
      const response = await beveragesLineService.getVerticalLineData(
        selectedProductionLineId,
        selectedObjectId,
        startDate,
        endDate
      );

      setVerticalLineTimeStamps(response.data.lineTimestamps || []);

      if (
        response.data.cipLineTimestampsDetailed &&
        response.data.cipLineTimestampsDetailed.length > 0
      ) {
        setCipLineTimestampsDetailed(response.data.cipLineTimestampsDetailed);
      } else {
        setCipLineTimestampsDetailed([]);
      }

      return response.data.lineTimestamps || [];
    } catch (error) {
      console.error(error);
      setVerticalLineTimeStamps([]);
      setCipLineTimestampsDetailed([]);
      return [];
    }
  };

  const getAlertTimeStamps = async (
    selectedProductionLineId,
    selectedObjectId,
    startDate,
    endDate
  ) => {
    try {
      const response = await beveragesLineService.getAlertsTimestampsData(
        selectedProductionLineId,
        selectedObjectId,
        startDate,
        endDate
      );
      return response.data.lineTimestamps;
    } catch (error) {
      console.error(error);
      return [];
    }
  };

  const getObjectDetails = async (objectId, selectedProductionLineId) => {
    try {
      setIsObjectDetailsRequestInProgress(true);
      setObjectDetailsRequestError(false);
      const response = await beveragesLineService.getObjectDetails(
        objectId,
        selectedProductionLineId
      );
      setIsObjectDetailsRequestInProgress(false);
      setLineDetails(response.data);
      let preRinseMergedData = response.data.reduce((acc, obj, index) => {
        if (obj.stepGroup === "Pre Rinse") {
          if (index === 0) {
            acc.push({ ...obj, healthCount: 1 });
          } else {
            let preRinsedMergedObj = acc.find(
              (item) => item.stepGroup === "Pre Rinse"
            );
            if (preRinsedMergedObj) {
              setPreRinseCount((prevCount) => prevCount + 1);
              preRinsedMergedObj.solution += " + " + obj.solution;
              preRinsedMergedObj.recycledVolume += obj.recycledVolume;
              preRinsedMergedObj.drainedVolume += obj.drainedVolume;
              preRinsedMergedObj.volume += obj.volume;
              preRinsedMergedObj.duration += obj.duration;
              preRinsedMergedObj.alerts += obj.alerts;
              preRinsedMergedObj.health += obj.health;
              preRinsedMergedObj.healthCount += 1;
            }
          }
        } else {
          acc.push(obj);
        }
        return acc;
      }, []);
      preRinseMergedData = preRinseMergedData.map((obj) => {
        if (obj.stepGroup === "Pre Rinse" && obj.healthCount) {
          obj.health = Math.round(obj.health / obj.healthCount);
          delete obj.healthCount;
        }
        return obj;
      });
      setLineDetailsPreRinseMerged(preRinseMergedData);
    } catch (error) {
      setIsObjectDetailsRequestInProgress(false);
      setObjectDetailsRequestError(true);
      console.error(error);
    }
  };

  const durationFormatter = (minutes) => {
    let days = Math.floor(minutes / 1440);
    let remainingMinutes = minutes % 1440;
    let hours = Math.floor(remainingMinutes / 60);
    let mins = remainingMinutes % 60;
    let daysStr = days > 0 ? String(days) + "d " : "";
    let hoursStr = String(hours).padStart(2, "0");
    let minsStr = String(mins).padStart(2, "0");
    return daysStr + hoursStr + ":" + minsStr;
  };

  const fetchObjectData = async (objectId) => {
    try {
      setIsObjectDataRequestInProgress(true);
      setObjectDataRequestError(false);
      setSensorChartsRequestError(false);
      const response = await beveragesLineService.getObjectDetailsById(
        objectId,
        selectedProductionLineId
      );
      if (response.data) {
        const data = response.data;
        setObjectDetails(data);
        setObjectName(data.objectName || "");
        setStartTime(data.startTime || "");
        setStartTimeFormatted(formatDateAndTime(data.startTime) || "");
        setEndTime(data.endTime || "");
        setDuration(removeFractionDigits(data.duration / 60) || 0);
        setRecipe(data.recipe || "");
        setKpi1(data.kpi1 || 0);
        setHealth(data.health || 0);
        setRecuperatedVolume(data.recuperatedVolume || 0);
        setFreshWaterVolume(data.freshWaterVolume || 0);
        setIsActive(data.isActive || false);
        setStepGroup(data.stepGroup || "");
        setAlerts(data.alerts || 0);
        setLine(data.line || "");
      }
      setIsObjectDataRequestInProgress(false);
    } catch (error) {
      setIsObjectDataRequestInProgress(false);
      setObjectDataRequestError(true);
      setSensorChartsRequestError(true);
      console.error("Error fetching object data:", error);
    }
  };

  useEffect(() => {
    if (
      !selectedProductionLineId ||
      !objectId ||
      !startTime ||
      !endTime ||
      !line
    )
      return;
    getMetersFlowRateData(
      selectedProductionLineId,
      objectId,
      startTime,
      endTime,
      unit,
      line
    );
  }, [selectedProductionLineId, objectId, startTime, endTime, unit, line]);

  useEffect(() => {
    if (objectId) {
      fetchObjectData(objectId);
      getObjectDetails(objectId, selectedProductionLineId);
    }
  }, [objectId, selectedProductionLineId, unit]);

  return (
    <Card style={{ backgroundColor: "#f5f7fb" }} bsPrefix={"main-card-style"}>
      <Modal
        show={showChartModal}
        onHide={handleClose}
        dialogClassName="modal-size"
        aria-labelledby="example-custom-modal-styling-title"
      >
        <Modal.Header closeButton></Modal.Header>
        <Modal.Body>
          <Row>
            <Col md={{ offset: 2 }}>
              <SensorCharts
                sensorData={sensorResponse}
                showSpinnerOnLoad={true}
                yAxisTitle="Flowrate"
                tooltipFormatter={null}
                multipleYAxis={false}
                isModal={true}
                verticalLineTimestamps={verticalLineTimeStamps}
                cipLineTimestampsDetailed={cipLineTimestampsDetailed}
              />
            </Col>
          </Row>
        </Modal.Body>
      </Modal>
      <AlertsModal
        isAlertsRequestInProgress={isStepAlertsRequestInProgress}
        alertsData={alertsData}
        show={showModal}
        setShow={setShowModal}
        alertsColumns={alertsColumns}
      />
      <Card.Body className={"p-0"}>
        <Button variant={"link"} onClick={history.goBack}>
          <FaArrowLeft
            style={{ color: "black", position: "absolute", left: 20, top: 70 }}
            size={20}
          />
        </Button>
        <Row className="h-100">
          <Col md={5} style={{ paddingLeft: "35px" }}>
            {isObjectDataRequestInProgress ? (
              <div
                style={{
                  height: "400px",
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                }}
              >
                <Spinner animation="border" variant="primary" />
              </div>
            ) : objectDataRequestError ? (
              <div
                style={{
                  height: "400px",
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                  flexDirection: "column",
                }}
              >
                <div className="text-danger mb-2">Failed to load data</div>
                <Button
                  variant="outline-primary"
                  onClick={() => fetchObjectData(objectId)}
                >
                  Retry
                </Button>
              </div>
            ) : (
              <Card className="h-100">
                <Card.Body className={"pl-3 pr-3 pt-2 pb-2"}>
                  <Row className={"pb-3 pt-3"}>
                    <Col>
                      <div
                        style={{
                          color: "grey",
                          fontWeight: 800,
                          textAlign: "center",
                        }}
                      >
                        Object
                      </div>
                      <div>
                        <h5
                          className={"mt-2"}
                          style={{
                            fontWeight: "bold",
                            textAlign: "center",
                          }}
                        >
                          {objectName}
                        </h5>
                      </div>
                    </Col>
                    <Col>
                      <div
                        style={{
                          color: "grey",
                          fontWeight: 800,
                          textAlign: "center",
                        }}
                      >
                        Start Time
                      </div>
                      <div>
                        <h5
                          className={"mt-2"}
                          style={{
                            fontWeight: "bold",
                            textAlign: "center",
                          }}
                        >
                          {startTimeFormatted}
                        </h5>
                      </div>
                    </Col>
                    <Col>
                      <div
                        style={{
                          color: "grey",
                          fontWeight: 800,
                          textAlign: "center",
                        }}
                      >
                        Duration [HH:mm]
                      </div>
                      <div>
                        <h5
                          className={"ml-2 mt-2"}
                          style={{
                            fontWeight: "bold",
                            textAlign: "center",
                          }}
                        >
                          {durationFormatter(duration)}
                        </h5>
                      </div>
                    </Col>
                  </Row>
                  <hr />
                  <Row className={"pt-4 pb-3"}>
                    <Col>
                      <div
                        style={{
                          color: "grey",
                          fontWeight: 800,
                          textAlign: "center",
                        }}
                      >
                        Recipe
                      </div>
                      <div>
                        <div
                          className={"mt-2"}
                          style={{
                            fontWeight: "bold",
                            whiteSpace: "nowrap",
                          }}
                        >
                          {recipe}
                        </div>
                      </div>
                    </Col>
                    <Col>
                      <div
                        style={{
                          color: "grey",
                          fontWeight: 800,
                          textAlign: "center",
                        }}
                      >
                        Total Fresh Water [
                        <h5 style={{ display: "inline-block" }}>
                          {cubicUnitLabel}
                        </h5>
                        ]
                      </div>
                      <div>
                        <h5
                          className={"mt-4"}
                          style={{
                            fontWeight: "bold",
                            textAlign: "center",
                          }}
                        >
                          {isImperial
                            ? parseFloat(
                                convertLitersToCubic(kpi1, 1)
                              ).toLocaleString(undefined, {
                                minimumFractionDigits: 0,
                                maximumFractionDigits: 0,
                              })
                            : parseFloat(
                                convertLitersToCubic(kpi1, 1)
                              ).toLocaleString()}{" "}
                        </h5>
                      </div>
                    </Col>
                    <Col>
                      <div
                        style={{
                          color: "grey",
                          fontWeight: 800,
                          textAlign: "center",
                        }}
                      >
                        Health Score
                      </div>
                      <div>
                        <div className={"mt-2"} style={{ fontWeight: "bold" }}>
                          {healthFormatter(health)}
                        </div>
                      </div>
                    </Col>
                  </Row>
                  <hr />
                  <Row style={{ paddingBottom: "9px" }}>
                    <Col className={"mt-3"}>
                      <div
                        style={{
                          color: "grey",
                          fontWeight: 800,
                          textAlign: "center",
                        }}
                      >
                        Percentage Recuperated
                      </div>
                      <div>
                        <h5
                          className={"mt-4"}
                          style={{
                            fontWeight: "bold",
                            textAlign: "center",
                          }}
                        >
                          {calculateRecuperatedWater().toLocaleString()}%
                        </h5>
                      </div>
                    </Col>
                    <Col className={"mt-3"}>
                      <div
                        style={{
                          color: "grey",
                          whiteSpace: "nowrap",
                          fontWeight: 800,
                          textAlign: "center",
                        }}
                      >
                        Current Step Group
                      </div>
                      <div>
                        <h5
                          className={"mt-4"}
                          style={{
                            fontWeight: "bold",
                            textAlign: "center",
                          }}
                        >
                          {handleStepGroupDisplayInfo()}
                        </h5>
                      </div>
                    </Col>
                    <Col className={"mt-3"}>
                      <div
                        style={{
                          color: "grey",
                          fontWeight: 800,
                          textAlign: "center",
                        }}
                      >
                        Alerts Found
                      </div>
                      <div>
                        <h5
                          className={"mt-4"}
                          style={{ fontWeight: "bold", textAlign: "center" }}
                        >
                          {alerts}
                        </h5>
                      </div>
                    </Col>
                  </Row>
                </Card.Body>
              </Card>
            )}
          </Col>
          <Col md={7}>
            <Card className="h-100">
              <Card.Body className={"p-2"}>
                <Row>
                  <Col md={{ offset: 9 }}>
                    {!sensorChartsRequestError &&
                      !isSensorChartsRequestInProgress &&
                      sensorChartsUnitSymbol !== null && (
                        <Button onClick={() => handleShow()}>
                          View all charts
                        </Button>
                      )}
                  </Col>
                </Row>
                <Row>
                  <Col>
                    {!sensorChartsRequestError &&
                    !isSensorChartsRequestInProgress &&
                    sensorChartsUnitSymbol !== null ? (
                      <FlowrateChartGroup
                        showLegend
                        linked
                        charts={sensorCharts}
                        unitSymbol={sensorChartsUnitSymbol}
                        yAxisTitle={"Flowrate"}
                        verticalLineTimestamps={verticalLineTimeStamps}
                        cipLineTimestampsDetailed={cipLineTimestampsDetailed}
                        showSpinnerOnLoad
                        plotlines={true}
                      />
                    ) : sensorChartsRequestError ? (
                      <div
                        style={{
                          height: "400px",
                          display: "flex",
                          justifyContent: "center",
                          alignItems: "center",
                          flexDirection: "column",
                        }}
                      >
                        <div className="text-danger mb-2">
                          Failed to load charts
                        </div>
                        {selectedProductionLineId &&
                          objectId &&
                          startTime &&
                          endTime && (
                            <Button
                              variant="outline-primary"
                              onClick={() => {
                                getMetersFlowRateData(
                                  selectedProductionLineId,
                                  objectId,
                                  startTime,
                                  endTime,
                                  unit
                                );
                              }}
                            >
                              Retry
                            </Button>
                          )}
                      </div>
                    ) : (
                      <div
                        style={{
                          height: "400px",
                          display: "flex",
                          justifyContent: "center",
                          alignItems: "center",
                        }}
                      >
                        <Spinner animation={"border"} variant={"primary"} />
                      </div>
                    )}
                  </Col>
                </Row>
              </Card.Body>
            </Card>
          </Col>
        </Row>
        <Row className={"pt-2"}>
          <Col>
            {isObjectDetailsRequestInProgress ? (
              <div
                style={{
                  height: "200px",
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                }}
              >
                <Spinner animation="border" variant="primary" />
              </div>
            ) : objectDetailsRequestError ? (
              <div
                style={{
                  height: "200px",
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                  flexDirection: "column",
                }}
              >
                <div className="text-danger mb-2">Failed to load details</div>
                <Button
                  variant="outline-primary"
                  onClick={() =>
                    getObjectDetails(objectId, selectedProductionLineId)
                  }
                >
                  Retry
                </Button>
              </div>
            ) : (
              <DataTable
                key={unit}
                headerStyle={headerStyle}
                colStyle={colStyle}
                data={
                  showPreRinseMerged && preRinseCount > 0
                    ? lineDetailsPreRinseMerged
                    : lineDetails
                }
                columns={columns}
                pageSize={lineDetails.length > 5 ? 4 : 5}
                paginationSize={10}
              />
            )}
          </Col>
        </Row>
      </Card.Body>
    </Card>
  );
}
